import React, { useContext, useEffect, useState } from "react";
import Roles from "../../apps/Admin/constants/roles";
import { GeneralObject } from "../../definitions/general";
import { useAuthenticatedApi, useZoAuthenticatedApi } from "../../hooks/useApi";
import { AuthContext } from "../auth";
import { AuthContext as ZoAuthContext } from "../zoauth";
import ProfileContext from "./ProfileContext";

interface ProfileProviderProps {}

interface Association {
  access_group: {
    name: string;
    key: string;
  };
  key: string;
  value: string;
  model: string;
}

const ProfileProvider: React.FC<ProfileProviderProps> = ({ children }) => {
  const [profile, setProfile] = useState({});
  const [zoProfile, setZoProfile] = useState({});
  const { logout } = useContext(AuthContext);
  const { logout: zoLogout } = useContext(ZoAuthContext);
  const [isLoggedOut, setIsLoggedOut] = useState(false);

  const api = useAuthenticatedApi("PROFILE_ME");
  const zoApi = useZoAuthenticatedApi("ZO_PROFILE_ME");
  const myAssociationsApi = useAuthenticatedApi("AUTHORIZATION_MY_ASSOCIATION");

  useEffect(() => {
    const isAdminDomain =
      window.location.host.includes("admin.zostel.com") ||
      (window.location.hostname === "localhost" &&
        window.location.pathname.startsWith("/admin"));

    if (isAdminDomain && myAssociationsApi) {
      myAssociationsApi.get("/").then((res) => {
        const associations: Association[] = res.data.associations || [];

        localStorage.setItem(
          "operatorId",
          JSON.stringify(
            associations
              .filter(
                (a) =>
                  a.model === "Operator" &&
                  a.access_group.key === Roles.PARTNER_CHECKIN
              )
              .map((a) => a.value)
          )
        );
      });
    }
  }, [myAssociationsApi]);

  useEffect(() => {
    if (api && !isLoggedOut) {
      api
        .get("")
        .then((res) => {
          setProfile(res.data.profile);
        })
        .catch((error) => {
          if (
            error.response?.status === 401 ||
            error.response?.status === 403
          ) {
            if (!isLoggedOut) {
              setIsLoggedOut(true);
              logout();
              zoLogout();
            }
          } else {
            console.error("API Error:", error);
          }
        });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [api]);

  useEffect(() => {
    if (zoApi && !isLoggedOut) {
      zoApi
        .get("")
        .then((res) => {
          setZoProfile(res.data);
        })
        .catch((error) => {
          if (
            error.response?.status === 401 ||
            error.response?.status === 403
          ) {
            if (!isLoggedOut) {
              setIsLoggedOut(true);
              logout();
              zoLogout();
            }
          } else {
            console.error("API Error:", error);
          }
        });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [zoApi]);

  const updateZoProfile = (updatedProfile: GeneralObject) => {
    if (zoApi) {
      zoApi
        .post("", updatedProfile)
        .then((res) => res.status === 200 && setZoProfile(res.data))
        .catch((error) => {
          if (
            error.response?.status === 401 ||
            error.response?.status === 403
          ) {
            if (!isLoggedOut) {
              setIsLoggedOut(true);
              logout();
              zoLogout();
              window.location.reload();
            }
          } else {
            console.error("API Error:", error);
          }
        });
    }
  };

  const updateProfile = (updatedProfile: GeneralObject) => {
    if (api) {
      console.log("Payload", updatedProfile);
      api
        .post("", updatedProfile)
        .then((res) => res.status === 200 && setProfile(res.data.profile))
        .catch((error) => {
          if (
            error.response?.status === 401 ||
            error.response?.status === 403
          ) {
            if (!isLoggedOut) {
              setIsLoggedOut(true);
              logout();
              zoLogout();
              window.location.reload();
            }
          } else {
            console.error("API Error:", error);
          }
        });
    }
  };

  const refetchProfile = () => {
    if (api) {
      api
        .get("")
        .then((res) => {
          setProfile(res.data.profile);
        })
        .catch((error) => {
          if (
            error.response?.status === 401 ||
            error.response?.status === 403
          ) {
            if (!isLoggedOut) {
              setIsLoggedOut(true);
              logout();
              zoLogout();
              window.location.reload();
            }
          } else {
            console.error("API Error:", error);
          }
        });
    }
  };

  return (
    <ProfileContext.Provider
      value={{
        profile,
        updateProfile,
        zoProfile,
        updateZoProfile,
        refetchProfile,
      }}
    >
      {children}
    </ProfileContext.Provider>
  );
};

export default ProfileProvider;
